import { safeSessionStorage } from '../lib/safeStorage';
import SpeechInput from '../components/SpeechInput';
import { ideaDiscoveryPrefill } from '../lib/ideaDiscovery';
import { ideaToProject } from '../lib/ideaProject';
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { apiErrorMessage } from '../lib/apiError';
import { isVenuePaidRequired, isVenueApprovalRequired } from '../lib/entitlements';
import RequestError from '../components/RequestError';
import { projectSearchPrefill } from '../lib/projectSearch';
import { PlanQuotaBadge } from '../components/PlanBadges';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Lightbulb, Sparkles, Wand2, ArrowLeft, ArrowRight, Users, MapPin, Ticket, CheckCircle2,
  AlertTriangle, ListChecks, ChevronDown, Loader2, Tag, Send, MessageSquare, RefreshCw,
} from 'lucide-react';
import { EVENT_TYPES_CANON, DISTRICTS_CANON, EVENT_TYPE_LABELS } from '../constants/canonicalEnums';
import { MATCH_FEATURE_NAME } from '../config/brand';

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY_EXTRA = { target_audience: '', district: '', capacity: '', budget: '', ticket_price: '', event_category: '' };

const MODES = [
  { key: 'find', icon: Sparkles, title: 'Bana Fikir Bul', desc: 'Kısa bir anlatım yap, sana birbirinden farklı en fazla 3 konsept önereyim.' },
  { key: 'improve', icon: Wand2, title: 'Fikrimi Geliştir', desc: 'Fikrini anlat; analiz edip daha uygulanabilir hâle getireyim.' },
];

const inputCls = 'w-full px-3.5 py-2.5 border border-zinc-200 rounded-lg bg-white text-sm focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 transition-all';

const fmtPrice = (min, max) => {
  if (min == null && max == null) return null;
  if (min != null && max != null) return min === 0 && max === 0 ? 'Ücretsiz' : `${min}–${max} TL`;
  return `${min ?? max} TL`;
};

/* --- Karar kartı: başlık > 1 cümle > 4 metadata > 2 kısa alan; uzun içerik "Detayları Gör" altında --- */
const Meta = ({ icon: Icon, label, value }) => (
  <div className="min-w-0">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 flex items-center gap-1"><Icon size={11} /> {label}</p>
    <p className="text-sm text-zinc-800 truncate mt-0.5" title={value || '—'}>{value || '—'}</p>
  </div>
);

const Bullets = ({ icon: Icon, cls, label, items, max }) => items?.length > 0 && (
  <div>
    <p className={`text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1 mb-1.5 ${cls}`}><Icon size={12} /> {label}</p>
    <ul className="space-y-1 text-[13px] text-zinc-600 leading-snug">
      {items.slice(0, max).map((t, i) => <li key={i} className="flex gap-1.5"><span className="text-zinc-300">•</span><span>{t}</span></li>)}
    </ul>
  </div>
);

const IdeaCard = ({ idea, index, onConvert, converting, updated, actionLabel = 'Projeye Dönüştür' }) => {
  const [open, setOpen] = useState(false);
  const price = fmtPrice(idea.suggested_ticket_price_min, idea.suggested_ticket_price_max);
  return (
    <article className="bg-white border border-zinc-200 rounded-2xl p-5 sm:p-6 flex flex-col hover:border-[#1E3A8A]/40 transition-colors"
      data-testid={`idea-card-${idea.title}`}>
      <div className="flex items-start justify-between gap-3">
        <span className="text-[11px] font-semibold text-[#FF7A00] inline-flex items-center gap-2">
          Fikir {index + 1}
          {updated && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`idea-updated-${idea.idea_id}`}>Güncellendi</span>}
        </span>
        {idea.event_type && (
          <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#E6EEFF] text-[#1E3A8A] shrink-0 inline-flex items-center gap-1">
            <Tag size={10} /> {EVENT_TYPE_LABELS[idea.event_type] || idea.event_type}
          </span>
        )}
      </div>
      <h3 className="font-serif text-[22px] leading-tight tracking-tight text-zinc-900 mt-1.5">{idea.title}</h3>
      <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{idea.short_description}</p>

      <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-5 pt-4 border-t border-zinc-100">
        <Meta icon={Users} label="Hedef kitle" value={idea.target_audience} />
        <Meta icon={Users} label="Kapasite" value={idea.suggested_capacity ? `~${idea.suggested_capacity} kişi` : null} />
        <Meta icon={Ticket} label="Bilet" value={price} />
        <Meta icon={MapPin} label="İlçe" value={idea.suggested_districts?.length ? idea.suggested_districts.join(', ') : null} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        <Bullets icon={CheckCircle2} cls="text-emerald-700" label="Neden çalışır" items={idea.why_it_can_work} max={2} />
        <Bullets icon={AlertTriangle} cls="text-amber-700" label="Dikkat edilecekler" items={idea.main_risks} max={2} />
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-zinc-100 space-y-4" data-testid={`idea-details-${idea.title}`}>
          <p className="text-[13px] text-zinc-600 leading-relaxed">{idea.concept}</p>
          <Bullets icon={ListChecks} cls="text-[#1E3A8A]" label="Sonraki adımlar" items={idea.next_steps} max={4} />
          {(idea.why_it_can_work?.length > 2 || idea.main_risks?.length > 2) && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Bullets icon={CheckCircle2} cls="text-emerald-700" label="Diğer güçlü yanlar" items={idea.why_it_can_work?.slice(2)} max={4} />
              <Bullets icon={AlertTriangle} cls="text-amber-700" label="Diğer riskler" items={idea.main_risks?.slice(2)} max={4} />
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mt-auto pt-5">
        <button type="button" onClick={() => setOpen(o => !o)} className="text-xs font-medium text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 whitespace-nowrap"
          data-testid={`idea-toggle-details-${idea.title}`}>
          <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} /> {open ? 'Detayları Gizle' : 'Detayları Gör'}
        </button>
        <button type="button" onClick={() => onConvert(idea)} disabled={!!converting}
          className="h-9 px-3.5 rounded-lg bg-[#1E3A8A] text-white text-[13px] font-medium inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 hover:bg-[#16307A] disabled:opacity-60 transition-colors"
          data-testid={`idea-convert-${idea.title}`}>
          {converting === idea.title ? <Loader2 size={14} className="animate-spin" /> : null} {actionLabel} <ArrowRight size={14} />
        </button>
      </div>
    </article>
  );
};

const OrganizerIdea = ({ role = 'organizer', ownerId = '', onLocked }) => {
  // A6: mekânda sunucu 403 VENUE_PAID_REQUIRED / VENUE_APPROVAL_REQUIRED dönerse sayfa kilit kartına geçer (VenueIdea).
  const venueLock = err => role === 'venue' && onLocked && (isVenueApprovalRequired(err) ? 'approval' : isVenuePaidRequired(err) ? 'paid' : null);
  const isVenue = role === 'venue';
  const basePath = `/${role}/idea`;
  const apiBase = `${API}/api/${role}/ideas`;
  const IDEA_SESSION_KEY = isVenue ? `mekandapro:idea:venue:${ownerId}:sessionId` : 'mekandapro:idea:sessionId';
  const IDEA_DRAFT_KEY = isVenue ? `mekandapro:idea:venue:${ownerId}:draft` : 'mekandapro:idea:draft';
  const [venue, setVenue] = useState(null);
  const [profileLoading, setProfileLoading] = useState(isVenue);
  const [profileError, setProfileError] = useState('');
  const [profileRetry, setProfileRetry] = useState(0);
  React.useEffect(() => {
    if (!isVenue) return;
    const controller = new AbortController();
    setProfileLoading(true); setProfileError('');
    axios.get(`${API}/api/venues/me`, { signal: controller.signal, timeout: 15000 })
      .then(({ data }) => { if (!controller.signal.aborted) setVenue(data); })
      .catch(err => { if (!controller.signal.aborted) setProfileError(apiErrorMessage(err, 'Mekân profili yüklenemedi. Yeniden deneyebilirsin.')); })
      .finally(() => { if (!controller.signal.aborted) setProfileLoading(false); });
    return () => controller.abort();
  }, [isVenue, ownerId, profileRetry]);
  const profileReady = !isVenue || (!profileLoading && !profileError && venue?.semt && venue?.max_capacity);

  const navigate = useNavigate();
  const location = useLocation();
  const homeEntry = location.state;
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(null);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [freeText, setFreeText] = useState('');
  const [extra, setExtra] = useState(EMPTY_EXTRA);
  const [showExtra, setShowExtra] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [quotaTick, setQuotaTick] = useState(0);
  const [result, setResult] = useState(null);
  const [converting, setConverting] = useState(null);
  const [created, setCreated] = useState(null); // {project, idea}
  const [sessionId, setSessionId] = useState(null);
  const [chat, setChat] = useState([]);          // [{role, content}] — görünüm için; geçmiş backend'de
  const [chatText, setChatText] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [updatedIds, setUpdatedIds] = useState(new Set());
  const chatEndRef = React.useRef(null);
  const [recent, setRecent] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  const requestEpoch = React.useRef(0);

  const startNewWork = React.useCallback((entry = null) => {
    requestEpoch.current += 1;
    safeSessionStorage.removeItem(IDEA_SESSION_KEY);
    safeSessionStorage.removeItem(IDEA_DRAFT_KEY);
    setMode(entry ? 'find' : null); setFreeText(entry?.homeBrief || '');
    setExtra(EMPTY_EXTRA); setShowExtra(false); setResult(null); setCreated(null);
    setSessionId(null); setChat([]); setChatText(''); setUpdatedIds(new Set());
    setRequestError(''); setLoading(false); setChatBusy(false); setConverting(null);
    setSending(false);
    setHydrated(true);
    navigate(basePath, { replace: true, state: null });
  }, [navigate, basePath, IDEA_SESSION_KEY, IDEA_DRAFT_KEY]);

  const applySession = React.useCallback((data) => {
    const inputs = data.inputs || {};
    setMode(data.mode || inputs.mode || null);
    setFreeText(inputs.free_text || '');
    setExtra({
      target_audience: inputs.target_audience || '',
      district: inputs.district || '',
      capacity: inputs.capacity ?? '',
      budget: inputs.budget || '',
      ticket_price: inputs.ticket_price ?? '',
      event_category: inputs.event_category || '',
    });
    setShowExtra(Boolean(inputs.target_audience || inputs.district || inputs.capacity || inputs.budget || inputs.ticket_price || inputs.event_category));
    setSessionId(data.id);
    setResult({ mode: data.mode, in_scope: true, message: '', ideas: data.ideas || [] });
    setChat((data.messages || []).slice(2).map(m => ({ role: m.role, content: m.content, action: m.action })));
    setUpdatedIds(new Set());
    setCreated(null);
    safeSessionStorage.setItem(IDEA_SESSION_KEY, data.id);
    safeSessionStorage.removeItem(IDEA_DRAFT_KEY);
    navigate(`${basePath}?session=${encodeURIComponent(data.id)}`, { replace: true });
  }, [navigate, basePath, IDEA_SESSION_KEY, IDEA_DRAFT_KEY]);

  const loadSession = React.useCallback(async (id, silent404 = false) => {
    const epoch = ++requestEpoch.current;
    try {
      const { data } = await axios.get(`${apiBase}/sessions/${id}`, { timeout: 20000 });
      if (epoch !== requestEpoch.current) return false;
      applySession(data);
      return true;
    } catch (err) {
      if (epoch !== requestEpoch.current) return false;
      if (err.response?.status === 404) {
        safeSessionStorage.removeItem(IDEA_SESSION_KEY);
        if (!silent404) toast.error('Fikir oturumu bulunamadı.');
      } else if (!silent404) {
        toast.error('Oturum yüklenemedi.');
      }
      return false;
    }
  }, [applySession, apiBase, IDEA_SESSION_KEY]);

  React.useEffect(() => {
    let active = true;
    const restore = async () => {
      // Explicit new work from the home composer must not restore an older session.
      if (homeEntry?.startFresh || searchParams.get('new') === '1') return;
      const sid = searchParams.get('session') || safeSessionStorage.getItem(IDEA_SESSION_KEY);
      if (sid) {
        await loadSession(sid, true);
      } else {
        try {
          const draft = JSON.parse(safeSessionStorage.getItem(IDEA_DRAFT_KEY) || 'null');
          if (draft) {
            setMode(draft.mode || null);
            setFreeText(draft.free_text || '');
            setExtra({ ...EMPTY_EXTRA, ...(draft.extra || {}) });
            setShowExtra(Boolean(draft.showExtra));
          }
        } catch { safeSessionStorage.removeItem(IDEA_DRAFT_KEY); }
      }
      if (active) setHydrated(true);
    };
    restore();
    return () => { active = false; requestEpoch.current += 1; };
    // Restore only on mount; subsequent URL changes are driven by applySession/startNewWork.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (homeEntry?.startFresh || new URLSearchParams(location.search).get('new') === '1') {
      startNewWork(homeEntry || {});
    }
  }, [location.key, location.search, homeEntry, startNewWork]);

  React.useEffect(() => {
    if (!hydrated || sessionId) return;
    if (!mode && !freeText && !Object.values(extra).some(Boolean)) {
      safeSessionStorage.removeItem(IDEA_DRAFT_KEY);
      return;
    }
    safeSessionStorage.setItem(IDEA_DRAFT_KEY, JSON.stringify({ mode, free_text: freeText, extra, showExtra }));
  }, [hydrated, sessionId, mode, freeText, extra, showExtra, IDEA_DRAFT_KEY]);

  React.useEffect(() => {
    if (mode) return;
    axios.get(`${apiBase}/sessions`).then(({ data }) => setRecent(data)).catch(() => setRecent([]));
  }, [mode, apiBase]);

  const openSession = async (id) => { await loadSession(id); };

  const generate = async (e) => {
    e?.preventDefault();
    if (voiceBusy || loading || chatBusy || converting || !profileReady) return;
    setRequestError('');
    if (freeText.trim().length < 3) { toast.error('Lütfen etkinliğini birkaç kelimeyle anlat.'); return; }
    setLoading(true);
    const epoch = ++requestEpoch.current;
    try {
      const payload = { mode, free_text: freeText.trim() };
      Object.entries(extra).forEach(([k, v]) => {
        if (v === '' || v === null) return;
        payload[k] = (k === 'capacity') ? parseInt(v, 10) : (k === 'ticket_price') ? parseFloat(v) : v;
      });
      if (isVenue) payload.district = venue.semt;
      const { data } = await axios.post(`${apiBase}/generate`, payload, { timeout: 45000 });
      if (epoch !== requestEpoch.current) return;
      setResult(data);
      setCreated(null);
      setSessionId(data.session_id || null);
      if (data.session_id) {
        safeSessionStorage.setItem(IDEA_SESSION_KEY, data.session_id);
        safeSessionStorage.removeItem(IDEA_DRAFT_KEY);
        navigate(`${basePath}?session=${encodeURIComponent(data.session_id)}`, { replace: true });
      }
      setChat([]); setUpdatedIds(new Set());
      if (!data.in_scope) toast.warning(data.message);
    } catch (err) {
      if (epoch !== requestEpoch.current) return;
      const lock = venueLock(err);
      if (lock) { onLocked(lock); return; }
      const message = apiErrorMessage(err, 'Fikir motoru şu an yanıt veremiyor, lütfen tekrar dene.');
      setRequestError(message);
      toast.error(message);
    } finally { if (epoch === requestEpoch.current) { setLoading(false); setQuotaTick(t => t + 1); } }
  };

  const convert = async (idea) => {
    if (voiceBusy || loading || chatBusy || converting || !profileReady) return;
    if (isVenue) {
      navigate('/venue/discovery', { state: { prefill: ideaDiscoveryPrefill(idea) } });
      return;
    }
    setConverting(idea.title);
    const epoch = ++requestEpoch.current;
    try {
      const { data } = await axios.post(`${API}/api/organizer/event-projects`, ideaToProject(idea, freeText));
      if (epoch !== requestEpoch.current) return;
      setCreated({ project: data, idea });
      toast.success('Etkinlik projen oluşturuldu.');
    } catch (err) {
      if (epoch !== requestEpoch.current) return;
      const d = err.response?.data?.detail;
      if (err.response?.status === 400 && String(d).includes('profil')) {
        toast.error('Projeye dönüştürmek için önce Profilim sayfasından organizatör profilini oluştur.');
      } else {
        toast.error(d || 'Proje oluşturulamadı, lütfen tekrar dene.');
      }
    } finally { if (epoch === requestEpoch.current) setConverting(null); }
  };

  const sendChat = async (text) => {
    const msg = (text || chatText).trim();
    if (!msg || !sessionId || chatBusy || loading || converting || !profileReady) return;
    setRequestError('');
    setChatBusy(true); setChatText('');
    setChat(c => [...c, { role: 'user', content: msg }]);
    const epoch = ++requestEpoch.current;
    try {
      const { data } = await axios.post(`${apiBase}/sessions/${sessionId}/messages`, { text: msg }, { timeout: 45000 });
      if (epoch !== requestEpoch.current) return;
      if (data.action !== 'answer') {
        const prev = new Map((result?.ideas || []).map(i => [i.idea_id, JSON.stringify(i)]));
        const changed = new Set(data.ideas.filter(i => prev.get(i.idea_id) !== JSON.stringify(i)).map(i => i.idea_id));
        setUpdatedIds(changed);
        setResult(r => ({ ...r, ideas: data.ideas, in_scope: true }));
      }
      setChat(c => [...c, { role: 'assistant', content: data.message, action: data.action }]);
    } catch (err) {
      if (epoch !== requestEpoch.current) return;
      setChat(c => c.slice(0, -1));
      setChatText(msg);
      const lock = venueLock(err);
      if (lock) { onLocked(lock); return; }
      const message = apiErrorMessage(err, 'Fikir motoru şu an yanıt veremiyor, lütfen tekrar dene.');
      setRequestError(message);
      toast.error(message);
    } finally {
      if (epoch === requestEpoch.current) {
        setChatBusy(false); setQuotaTick(t => t + 1);
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
      }
    }
  };

  const [sending, setSending] = useState(false);
  const goToMatch = async () => {
    const p = created.project;
    setSending(true);
    const epoch = ++requestEpoch.current;
    try {
      const { data } = await axios.post(`${API}/api/organizer/event-projects/${p.id}/send-to-matching`);
      if (epoch !== requestEpoch.current) return;
      toast.success('Projen mekân aramaya hazır.');
      navigate(`/organizer/search?project=${p.id}`, { state: { prefill: projectSearchPrefill(data.project || p) } });
    } catch (err) {
      if (epoch !== requestEpoch.current) return;
      toast.error(err.response?.data?.detail || 'Mekân aramaya geçilemedi. Projen korundu; tekrar deneyebilirsin.');
    } finally { if (epoch === requestEpoch.current) setSending(false); }

  };

  const activeMode = MODES.find(m => m.key === mode);
  const ideaCount = result?.ideas?.length || 0;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid={`${role}-idea-page`}>
      <div className="idea-page-heading mb-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#E6EEFF] flex items-center justify-center shrink-0">
          <Lightbulb size={20} strokeWidth={1.75} className="text-[#1E3A8A]" />
        </div>
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-zinc-900 leading-none">{isVenue ? 'Mekânıma fikir bul' : 'Fikir'}</h1>
          <p className="text-zinc-500 text-sm mt-1">{isVenue ? 'Kendi mekânında düzenleyebileceğin etkinlikleri keşfet ve birlikte geliştir.' : 'Yeni bir etkinlik fikri geliştir veya aklındaki fikri netleştir.'}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {(sessionId || mode || freeText) && (
            <button type="button" onClick={() => startNewWork()} disabled={loading || chatBusy || !!converting}
              className="text-xs px-3 py-1.5 rounded-lg border border-zinc-200 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50"
              data-testid="idea-new-work">
              Yeni çalışma başlat
            </button>
          )}
          <PlanQuotaBadge refreshKey={quotaTick}
            keys={[{ key: 'idea_generate_daily', label: 'fikir' }, { key: 'idea_revise_daily', label: 'revizyon' }]} />
        </div>
      </div>

      {isVenue && <section className="mb-6 rounded-2xl border border-zinc-200 bg-white p-4 sm:p-5" aria-label="Fikirlere temel olan mekân profili" data-testid="idea-venue-context">
        {profileLoading ? <p className="text-sm text-zinc-500" role="status">Mekân bilgilerin yükleniyor…</p> : profileError ? <>
          <p className="text-sm text-zinc-700" role="alert">{profileError}</p>
          <button type="button" className="text-sm text-[#1E3A8A] mt-2" onClick={() => setProfileRetry(n => n + 1)}>Yeniden dene</button>
        </> : profileReady ? <>
          <p className="font-semibold text-zinc-900 break-words">{venue.name}</p>
          <p className="text-sm text-zinc-600 mt-1">{venue.semt} · {venue.min_capacity_flexible ? 'En fazla ' : `${venue.min_capacity || 1}–`}{venue.max_capacity} kişi</p>
          {venue.accepted_event_types?.length > 0 && <p className="text-sm text-zinc-600 mt-2">{venue.accepted_event_types.map(t => EVENT_TYPE_LABELS[t] || t).join(' · ')}</p>}
          <p className="text-xs text-zinc-500 mt-2">Fikirlerde profilindeki olanaklar ve çalışma koşulları dikkate alınır. Gün ve saat tercihlerin kesin müsaitlik değildir.</p>
        </> : <p className="text-sm text-zinc-700">Mekânına uygun fikirler için önce ilçe ve kapasite bilgilerini tamamla.</p>}
        <Link to="/venue/profile" className="inline-flex text-sm font-medium text-[#1E3A8A] mt-3">Mekân profilimi düzenle <ArrowRight size={16} className="ml-1" /></Link>
      </section>}

      <RequestError message={requestError} testId="idea-error" />
      {!mode && recent.length > 0 && (
        <section className="mb-6" data-testid="idea-recent">
          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Son Çalışmalar</p>
          <ul className="bg-white border border-zinc-200 rounded-2xl divide-y divide-zinc-100">
            {recent.map(r => (
              <li key={r.id}>
                <button type="button" onClick={() => openSession(r.id)} className="w-full text-left px-4 py-3 hover:bg-zinc-50 flex items-center gap-3" data-testid={`idea-recent-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-zinc-900 truncate">{r.prompt || 'Fikir oturumu'}</p>
                    <p className="text-xs text-zinc-500 truncate">{r.idea_titles.join(' · ') || '—'}</p>
                  </div>
                  <span className="text-[11px] text-zinc-400 shrink-0">{r.updated_at ? new Date(r.updated_at).toLocaleDateString('tr-TR') : ''}</span>
                  <ArrowRight size={14} className="text-zinc-300 shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!mode && (
        <div className="grid gap-4 sm:grid-cols-2" data-testid="idea-mode-select">
          {MODES.map(({ key, icon: Icon, title, desc }) => (
            <button key={key} type="button" onClick={() => setMode(key)} data-testid={`idea-mode-${key}`}
              className="text-left bg-white border border-zinc-200 rounded-2xl p-6 hover:border-[#1E3A8A] hover:shadow-[0_8px_30px_-12px_rgba(30,58,138,0.25)] transition-all group">
              <Icon size={20} strokeWidth={1.75} className="text-[#1E3A8A] mb-4" />
              <h2 className="font-serif text-xl tracking-tight text-zinc-900 mb-1.5">{title}</h2>
              <p className="text-sm text-zinc-600 mb-4">{desc}</p>
              <span className="text-sm font-medium text-[#1E3A8A] inline-flex items-center gap-1">Başla <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" /></span>
            </button>
          ))}
        </div>
      )}

      {mode && !created && !sessionId && (
        <form onSubmit={generate} className="bg-white border border-zinc-200 rounded-2xl p-4 sm:p-5 space-y-3" data-testid="idea-form">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#1E3A8A] bg-[#E6EEFF] px-2.5 py-1 rounded-full inline-flex items-center gap-1" data-testid="idea-active-mode">
              {activeMode && <activeMode.icon size={12} />} {activeMode?.title}
            </span>
            {!sessionId && (
              <button type="button" disabled={voiceBusy || loading || chatBusy || !!converting} onClick={() => { setMode(null); setRequestError(''); }} className="text-xs text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1" data-testid="idea-change-mode">
                <ArrowLeft size={13} /> Seçimi değiştir
              </button>
            )}
          </div>
          <textarea value={freeText} onChange={e => setFreeText(e.target.value)} rows={3} required
            placeholder={isVenue ? 'Örn. Hafta içi boş saatlerimde mekânıma yeni ziyaretçiler getirecek üç farklı etkinlik fikri öner…' : 'Nasıl bir etkinlik yapmak istediğini anlat…'} className={`${inputCls} resize-none text-[15px]`} data-testid="idea-free-text" />
          <SpeechInput disabled={loading} onBusyChange={setVoiceBusy} onTranscript={text => setFreeText(prev => [prev.trim(), text].filter(Boolean).join(' ').slice(0, 4000))} />

          <button type="button" onClick={() => setShowExtra(s => !s)} className="text-[13px] text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1" data-testid="idea-toggle-extra">
            <ChevronDown size={14} className={`transition-transform ${showExtra ? 'rotate-180' : ''}`} /> İstersen birkaç detay ekle <span className="text-zinc-400">(opsiyonel)</span>
          </button>
          {showExtra && (
            <div className="grid sm:grid-cols-3 gap-2.5" data-testid="idea-extra-fields">
              <input className={inputCls} placeholder="Hedef kitle" value={extra.target_audience} onChange={e => setExtra({ ...extra, target_audience: e.target.value })} data-testid="idea-target-audience" />
              <select className={inputCls} disabled={isVenue} value={isVenue ? venue?.semt || '' : extra.district} onChange={e => setExtra({ ...extra, district: e.target.value })} data-testid="idea-district">
                <option value="">İlçe (İstanbul)</option>
                {DISTRICTS_CANON.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <input className={inputCls} type="number" min={isVenue && !venue?.min_capacity_flexible ? venue?.min_capacity || 1 : 1} max={isVenue ? venue?.max_capacity : undefined} placeholder="Tahmini kapasite" value={extra.capacity} onChange={e => setExtra({ ...extra, capacity: e.target.value })} data-testid="idea-capacity" />
              <input className={inputCls} placeholder="Yaklaşık bütçe (TL)" value={extra.budget} onChange={e => setExtra({ ...extra, budget: e.target.value })} data-testid="idea-budget" />
              <input className={inputCls} type="number" min="0" placeholder="Bilet fiyatı (TL)" value={extra.ticket_price} onChange={e => setExtra({ ...extra, ticket_price: e.target.value })} data-testid="idea-ticket-price" />
              <select className={inputCls} value={extra.event_category} onChange={e => setExtra({ ...extra, event_category: e.target.value })} data-testid="idea-category">
                <option value="">Etkinlik kategorisi</option>
                {EVENT_TYPES_CANON.filter(t => !isVenue || !venue?.accepted_event_types?.length || venue.accepted_event_types.includes(t.value)).map(t => <option key={t.value} value={t.label}>{t.label}</option>)}
              </select>
            </div>
          )}

          <div className="flex justify-end pt-1" data-testid="idea-form-actions">
            <button type="submit" disabled={voiceBusy || loading || chatBusy || !!converting || !profileReady}
              className="h-10 px-5 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-[#16307A] disabled:opacity-70 transition-colors w-full sm:w-auto justify-center"
              data-testid="idea-generate-button">
              {loading ? <><Loader2 size={15} className="animate-spin" /> Fikirler hazırlanıyor…</> : <><Sparkles size={15} /> Fikir Oluştur</>}
            </button>
          </div>
        </form>
      )}

      {result && !created && (
        <section className="mt-5" data-testid="idea-results">
          {result.in_scope ? (
            <>
              <div className="flex items-end justify-between gap-4 mb-3 px-1">
                <div>
                  <h2 className="font-serif text-xl sm:text-2xl tracking-tight text-zinc-900" data-testid="idea-result-title">
                    {mode === 'find' ? `Senin için ${ideaCount} farklı format hazırladım.` : 'Fikrinin geliştirilmiş hâli hazır.'}
                  </h2>
                  <p className="text-[13px] text-zinc-500 mt-0.5" data-testid="idea-result-message">{result.message}</p>
                </div>
                <span className="hidden sm:inline text-xs text-zinc-400 shrink-0">{isVenue ? 'Birini seç → Organizatör bul' : 'Birini seç → Projeye Dönüştür'}</span>
              </div>
              <div className={`grid gap-4 ${ideaCount > 1 ? 'lg:grid-cols-3 md:grid-cols-2' : 'max-w-2xl'}`}>
                {result.ideas.map((idea, i) => <IdeaCard key={idea.idea_id || idea.title} idea={idea} index={i} onConvert={convert} converting={converting || loading || chatBusy || !profileReady} actionLabel={isVenue ? 'Organizatör bul' : 'Projeye Dönüştür'} updated={updatedIds.has(idea.idea_id)} />)}
              </div>

              {sessionId && (
                <div className="mt-6 bg-white border border-zinc-200 rounded-2xl p-4 sm:p-5" data-testid="idea-chat">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare size={14} className="text-[#1E3A8A]" />
                    <p className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Fikirleri birlikte geliştirelim</p>
                    <span className="ml-auto text-[11px] text-zinc-400 hidden sm:inline">Kartlar her mesajda güncellenir</span>
                  </div>
                  {chat.length > 0 && (
                    <ul className="space-y-2 mb-3 max-h-56 overflow-y-auto pr-1" data-testid="idea-chat-log">
                      {chat.map((m, i) => (
                        <li key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <span className={`text-[13px] leading-snug px-3 py-2 rounded-xl max-w-[85%] ${m.role === 'user' ? 'bg-[#1E3A8A] text-white' : 'bg-zinc-50 border border-zinc-200 text-zinc-700'}`}
                            data-testid={`idea-chat-${m.role}-${i}`}>
                            {m.content}
                          </span>
                        </li>
                      ))}
                      {chatBusy && <li className="flex"><span className="text-[13px] px-3 py-2 rounded-xl bg-zinc-50 border border-zinc-200 text-zinc-500 inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Fikirler güncelleniyor…</span></li>}
                      <li ref={chatEndRef} />
                    </ul>
                  )}
                  <div className="flex flex-wrap gap-2 mb-3" data-testid="idea-quick-chips">
                    {['Daha ucuz yap', 'Daha yaratıcı yap', 'Daha kolay operasyon', 'Daha premium', 'Yeni 3 fikir'].map(c => (
                      <button key={c} type="button" onClick={() => sendChat(c)} disabled={chatBusy || loading || !!converting || !profileReady}
                        className="text-xs px-3 py-1.5 rounded-full border border-zinc-200 text-zinc-700 hover:border-[#1E3A8A] hover:text-[#1E3A8A] disabled:opacity-50 transition-colors inline-flex items-center gap-1"
                        data-testid={`idea-chip-${c}`}>
                        {c === 'Yeni 3 fikir' && <RefreshCw size={11} />}{c}
                      </button>
                    ))}
                  </div>
                  <form onSubmit={(e) => { e.preventDefault(); sendChat(); }} className="flex gap-2">
                    <input value={chatText} onChange={e => setChatText(e.target.value)} disabled={chatBusy || loading || !!converting || !profileReady}
                      placeholder="Fikirleri geliştirmek için devam et…" className={inputCls} data-testid="idea-chat-input" />
                    <button type="submit" disabled={chatBusy || loading || !!converting || !chatText.trim() || !profileReady}
                      className="h-10 w-10 shrink-0 rounded-lg bg-[#1E3A8A] text-white inline-flex items-center justify-center hover:bg-[#16307A] disabled:opacity-50"
                      aria-label="Gönder" data-testid="idea-chat-send">
                      {chatBusy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                    </button>
                  </form>
                </div>
              )}
            </>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-sm text-amber-900" data-testid="idea-out-of-scope">{result.message}</div>
          )}
        </section>
      )}

      {created && (
        <section className="bg-white border border-emerald-200 rounded-2xl p-8 text-center max-w-2xl mx-auto" data-testid="idea-created">
          <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={24} className="text-emerald-600" />
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-zinc-900 mb-1">Etkinlik projen oluşturuldu.</h2>
          <p className="text-sm text-zinc-600 mb-6">“{created.project.title}” artık Projelerim altında <span className="font-medium">Fikir</span> aşamasında.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/organizer/home" className="h-10 px-5 rounded-lg border border-zinc-200 text-sm font-medium text-zinc-800 inline-flex items-center justify-center gap-2 hover:bg-zinc-50" data-testid="idea-view-project">
              Projeyi Gör
            </Link>
            <button type="button" onClick={goToMatch} disabled={sending} className="h-10 px-5 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-[#16307A] disabled:opacity-60" data-testid="idea-go-match">
              {sending ? <Loader2 size={15} className="animate-spin" /> : null} {MATCH_FEATURE_NAME}’e Geç <ArrowRight size={15} />
            </button>
          </div>
          <button type="button" onClick={() => setCreated(null)} className="mt-5 text-xs text-zinc-500 hover:text-zinc-800" data-testid="idea-back-to-results">Diğer fikirlere dön</button>
        </section>
      )}
    </div>
  );
};

export default OrganizerIdea;

